import React from 'react'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import VehicleSnapshot from './VehicleSnapshot'
import GuessFeedbackPulse from './GuessFeedbackPulse'
import type { GuessEntry, GuessDirection, GuessFeedback, SoldVehicle } from '../types'
import { formatPrice } from '../format'
import PriceBillboard from './PriceBillboard'
import NeonProgress from './NeonProgress'
import { TV_COLORS, buzzerButton } from '../designSystem'
import PresenterPrompt from './PresenterPrompt'

type FinalPhaseProps = {
  vehicle: SoldVehicle | null
  remainingMs: number
  totalDurationMs: number
  guesses: Array<GuessEntry>
  lastDirection: GuessDirection | null
  feedback?: GuessFeedback | null
  onGuess: (value: number) => void
  revealPrice?: boolean
}

const FinalPhase = ({
  vehicle,
  remainingMs,
  totalDurationMs,
  guesses,
  lastDirection,
  feedback,
  onGuess,
  revealPrice = false,
}: FinalPhaseProps) => {
  const [value, setValue] = React.useState('')

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const sanitized = value.replace(/\s+/g, '')
    const numeric = Number(sanitized)
    if (Number.isNaN(numeric)) return
    onGuess(numeric)
    setValue('')
  }

  const progress = Math.max(0, Math.min(100, (remainingMs / totalDurationMs) * 100))
  const feedbackMap: Record<GuessDirection, string> = {
    higher: 'C’est plus haut !',
    lower: 'C’est plus bas !',
    correct: 'Juste prix trouvé !',
  }

  const getGuessIcon = (direction: GuessDirection) => {
    if (direction === 'correct') return '✅'
    return direction === 'higher' ? '↗' : '↘'
  }

  const getGuessLabel = (direction: GuessDirection) => {
    if (direction === 'correct') return 'Trouvé'
    return direction === 'higher' ? 'Plus demandé' : 'Moins demandé'
  }

  React.useEffect(() => {
    if (lastDirection === 'correct') {
      setValue('')
    }
  }, [lastDirection])

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      <VehicleSnapshot
        vehicle={vehicle}
        revealPrice={revealPrice}
      />

      <NeonProgress value={progress} />

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
        <PriceBillboard
          label="Ta proposition"
          value={value ? Number(value) : null}
          highlight
          editable
          inputValue={value}
          placeholder="Entrez un montant"
          onInputChange={(next) => setValue(next.replace(/[^0-9]/g, ''))}
        />
        <Button
          variant="contained"
          fullWidth
          type="submit"
          sx={{
            mt: 1,
            ...buzzerButton('higher'),
            background: `linear-gradient(120deg, ${TV_COLORS.blue}, ${TV_COLORS.violet})`,
          }}
        >
          Valider
        </Button>
      </Box>

      <PresenterPrompt message="Le chrono tourne… fais ton choix !" />

      <GuessFeedbackPulse feedback={feedback} />

      <Stack spacing={0.4} sx={{ fontSize: 12 }}>
        {guesses.map((guess) => (
          <Stack
            key={guess.id}
            direction="row"
            justifyContent="space-between"
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600 }}
            >
              {formatPrice(guess.value)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#fff' }}
            >
              {getGuessIcon(guess.direction)} {getGuessLabel(guess.direction)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

export default FinalPhase
